import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { select, Store } from "@ngrx/store";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { LocationsComponent } from "src/app/shared/components/locations/locations.component";
import { ObjectiveStatusInterface } from "src/app/shared/enum/objectives";
import {
  ObjectiveTypeEnum,
  ObjectiveTypeEnumMapping,
  ObjectiveMapping,
} from "src/app/shared/enum/objectives";
import { SelectedLps } from "src/app/shared/models/location-partners/selectedLps";
import { BadgeService } from "src/app/shared/services/badge.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import {
  calculateTotalLPPriceByCampanyNamesSelector,
  loadingViewsSelector,
  saveSelectedLPsSelector,
} from "src/app/store/locationPartner/location-partner.selectors";
import { CalculatePrice } from "src/app/shared/models/calcul/calculatePrice";
import { LocationSearchMethod } from "src/app/shared/enum/locationSearchMethod";
import { IColumn } from "src/app/shared/models/table/column.interface";
import { TotalPrice } from "src/app/shared/models/calcul/totalPrice";
import {
  selectedLPs,
  calculateTotalLPPriceByCampanyNames,
  invokeCalculateTotalLPPriceByCampanyNames,
} from "src/app/store/locationPartner/location-partner.action";
import { NewCampaignCalculatorService } from "src/app/shared/services/calculations/new-campaign.service";
import {
  catchForbiddenErrorSelector,
  catchServerErrorSelector,
} from "src/app/store/user/user.selectors";
import {
  catchForbiddenError,
  catchServerError,
} from "src/app/store/user/user.actions";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { LocationAvailabilityEnum } from "src/app/shared/enum/location-availability";
import { JwtService } from "src/app/shared/services/jwt.service";
import { Authorities } from "src/app/shared/enum/authorities";
import { TotalPriceWithDiscount } from "src/app/shared/models/calcul/totalPrice";
import { UtilFunctions } from "src/app/utils/utilFunctions";
import { environment } from "src/environments/environment";
@Component({
  selector: "app-calculator",
  templateUrl: "./calculator.component.html",
})
export class CalculatorComponent extends BaseComponent implements OnInit {
  locationSelected: TotalPrice;

  form: FormGroup;
  allObjectiveNames: ObjectiveStatusInterface[];
  selectedLps: SelectedLps = {
    companyNames: [],
    isCalculator: false,
    count: 0,
  };

  startDate: Date | null;
  endDate: Date | null;
  showSelectedLPs$: Observable<SelectedLps | null>;
  calculateTotalLpPrice$: Observable<TotalPriceWithDiscount | null>;
  totalPrice: TotalPrice[] = [];
  totalPriceExceptUnavailableLps: TotalPrice[] = [];
  calculatePrice: CalculatePrice = {
    calculationMethod: LocationSearchMethod.LOCATION_PARTNER,
    names: this.selectedLps.companyNames,
    objectiveName: ObjectiveTypeEnum.APP_PROMOTION_AND_INSTALL,
    startDate: null,
    endDate: null,
    checkDates: true,
  };

  columns: IColumn[] = [
    {
      key: "",
      label: "",
      sorter: true,
    },
    {
      key: "average views",
      label: "admin.campaigns.simulator.averageViews",
      sorter: true,
    },
    {
      key: "Price per day",
      label: "admin.campaigns.simulator.pricePerView",
      sorter: true,
    },
    {
      key: "Selling price",
      label: "admin.campaigns.simulator.sellingPrice",
      sorter: true,
    },
  ];
  objective = ObjectiveMapping;
  total = {
    totalAverageViewsPerDay: 0,
    totalPricePerView: 0,
    totalAverageViews: 0,
    totalPrice: 0,
    totalSellingPricePerDay: 0,
  };
  dateInBetween: number = 0;
  loading: boolean = false;
  loading$: Observable<boolean | null>;
  loadingFromServerErreur$: Observable<boolean>;
  today = new Date();

  catchServerError$: Observable<boolean | null>;
  costPerImpression: number = 0;
  catchForbiddenError$: Observable<boolean | null>;
  forbidden: boolean | null = false;
  clickEstimate = {
    min: 0,
    max: 0,
  };
  leadGenEstimate = {
    min: 0,
    max: 0,
  };
  ObjectiveTypeEnum = ObjectiveTypeEnum;
  Math = Math;

  objectiveName: ObjectiveTypeEnum;

  minAvgViews: number = 0;
  maxAvgViews: number = 0;
  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    public badgeService: BadgeService,
    private store: Store<AppStateInterface>,
    private newCampaignCalculatorService: NewCampaignCalculatorService,
    private saveHistory: SaveUserHistoryService,
    private jwtService: JwtService
  ) {
    super(store);
    this.form = this.fb.group({
      objective: ["", [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
    });
    this.showSelectedLPs$ = this.store
      .pipe(select(saveSelectedLPsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.calculateTotalLpPrice$ = this.store
      .pipe(select(calculateTotalLPPriceByCampanyNamesSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loading$ = this.store
      .pipe(select(loadingViewsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.catchServerError$ = this.store
      .pipe(select(catchServerErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.catchForbiddenError$ = this.store
      .pipe(select(catchForbiddenErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.allObjectiveNames = Object.keys(ObjectiveTypeEnum)
      .filter((value) => isNaN(Number(value)) === true)
      .map((key: string | any) => ({
        id: ObjectiveTypeEnum[key as keyof typeof ObjectiveTypeEnum],
        value: ObjectiveTypeEnumMapping[key as keyof typeof ObjectiveTypeEnum],
      }));
  }
  ngOnInit(): void {
    this.objectiveName = this.objective[this.form.get("objective")?.value];
    this.catchForbiddenError$.subscribe((result) => {
      if (result) {
        this.loading = false;
        const authorities = this.jwtService.getPermissions(
          this.jwtService.getToken()
        );
        if (authorities) {
          this.forbidden = !authorities.includes(
            Authorities.LP_GETCOST.toString()
          );
        }
        this.store.dispatch(catchForbiddenError({ forbiddenError: null }));
      }
    });
    this.catchServerError$.subscribe((result) => {
      if (result) {
        this.loading = false;
        this.store.dispatch(catchServerError({ serverError: null }));
      }
    });
    this.form.valueChanges.subscribe((value) => {
      this.startDate = value.startDate;
      this.endDate = value.endDate;
      this.objectiveName = this.objective[value.objective];
      if (value.startDate != null && value.endDate != null) {
        const oneDay = 24 * 60 * 60 * 1000;
        this.dateInBetween = Math.round(
          Math.abs(
            (value.endDate.getTime() - value.startDate.getTime()) / oneDay
          ) + 1
        );
      }
    });
    /*this.store.dispatch(
      calculateTotalLPCostByCampanyNames({ TotalPriceWithDiscount: null })
    );*/
    //this.loadingFromServerErreur$.subscribe((data)=>{this.loading = data ;
    this.loading$.subscribe((data) => {
      if (data) this.loading = data;
    });
    this.store.dispatch(selectedLPs({ selectedLps: null }));
    this.showSelectedLPs$.subscribe((result: SelectedLps | null) => {
      if (result) {
        this.totalPrice = [];
        this.totalPriceExceptUnavailableLps = [];
        this.total = {
          totalAverageViewsPerDay: 0,
          totalPricePerView: 0,
          totalAverageViews: 0,
          totalPrice: 0,
          totalSellingPricePerDay: 0,
        };
        this.selectedLps.companyNames = result.companyNames;
      }
    });
    this.calculateTotalLpPrice$.subscribe(
      (result: TotalPriceWithDiscount | null) => {
        this.loading = false;
        if (result) {
          this.totalPriceExceptUnavailableLps = result.totalPricePerDay.filter(
            (lp: TotalPrice) =>
              lp.availabilities.availabilityStatus !=
              LocationAvailabilityEnum.NOT_AVAILABLE
          );
          this.totalPrice = result.totalPricePerDay;
          this.total.totalSellingPricePerDay =
            this.newCampaignCalculatorService.calculateSellingPricePerDayByPriceDto(
              result
            );
          this.total.totalPrice =
            this.newCampaignCalculatorService.calculateTotalPrice(
              result,
              this.dateInBetween
            );

          this.total.totalAverageViewsPerDay =
            this.newCampaignCalculatorService.calculateImpressionsPerDay(
              result
            );

          this.total.totalAverageViews =
            this.newCampaignCalculatorService.calculateTotalImpressions(
              result,
              this.dateInBetween
            );

          this.total.totalPricePerView =
            this.newCampaignCalculatorService.calculatePricePerView(result);

          if (this.total.totalAverageViews != 0) {
            this.costPerImpression =
              this.total.totalPrice / this.total.totalAverageViews;

            this.minAvgViews = this.total.totalAverageViews * 0.95;
            this.maxAvgViews = this.total.totalAverageViews * 1.05;

            if (this.objectiveName === ObjectiveTypeEnum.WEB_SITE_TRAFFIC) {
              this.clickEstimate = {
                min: UtilFunctions.calculateAverage(
                  this.minAvgViews,
                  environment.MIN_ESTIMATED_CLICKS_WEBSITE_TRAFFIC
                ),
                max: UtilFunctions.calculateAverage(
                  this.maxAvgViews,
                  environment.MAX_ESTIMATED_CLICKS_WEBSITE_TRAFFIC
                ),
              };
            }
            if (
              this.objectiveName === ObjectiveTypeEnum.GENERATION_DE_PROSPECTS
            ) {
              this.clickEstimate = {
                min: UtilFunctions.calculateAverage(
                  this.minAvgViews,
                  environment.MIN_ESTIMATED_CLICKS_LEAD_GEN
                ),
                max: UtilFunctions.calculateAverage(
                  this.maxAvgViews,
                  environment.MAX_ESTIMATED_CLICKS_LEAD_GEN
                ),
              };
              this.leadGenEstimate = {
                min: UtilFunctions.calculateAverage(
                  this.minAvgViews,
                  environment.MIN_ESTIMATED_LEADS
                ),
                max: UtilFunctions.calculateAverage(
                  this.maxAvgViews,
                  environment.MAX_ESTIMATED_LEADS
                ),
              };
            }
          } else {
            this.clickEstimate = {
              min: 0,
              max: 0,
            };
            this.leadGenEstimate = {
              min: 0,
              max: 0,
            };
          }
        }
      }
    );
  }
  onClickSelectLps() {
    const modal = this.modalService.open(LocationsComponent, {
      centered: true,
      size: "lg",
    });
    this.selectedLps.isCalculator = true;
    modal.componentInstance.selectedLPs = this.selectedLps.isCalculator;
  }
  onClickCalculate() {
    this.calculatePrice = {
      calculationMethod: LocationSearchMethod.LOCATION_PARTNER,
      names: this.selectedLps.companyNames,
      objectiveName: this.objectiveName,
      startDate: this.form.get("startDate")?.value,
      endDate: this.form.get("endDate")?.value,
      checkDates: true,
    };
    this.store.dispatch(
      invokeCalculateTotalLPPriceByCampanyNames({
        calculatePrice: this.calculatePrice,
      })
    );
    this.saveHistory.saveUserHistory(
      UserHistoryActionsEnum.POST,
      UserHistoryServicesEnum.CALCULATE_COST
    );
  }
  onClickReset() {
    this.loading = false;
    this.form.reset({
      objective: "",
      startDate: null,
      endDate: null,
    });
    this.selectedLps.companyNames = [];
    this.totalPrice = [];
    this.total = {
      totalAverageViewsPerDay: 0,
      totalPricePerView: 0,
      totalAverageViews: 0,
      totalPrice: 0,
      totalSellingPricePerDay: 0,
    };
    this.costPerImpression = 0;
    this.clickEstimate = {
      min: 0,
      max: 0,
    };
    this.leadGenEstimate = {
      min: 0,
      max: 0,
    };
    this.store.dispatch(
      calculateTotalLPPriceByCampanyNames({ totalPriceWithDiscount: null })
    );
  }
}
